/**
 * Events list — renders assigned sampling events from Dexie. Plan-aware
 * tiles show site name, scheduled date, and a gradient progress bar
 * (wells_completed / total_wells). Tap-to-open routes to EventDetail.
 *
 * Premium polish pass (Tier 2.C): icon-button header actions, gradient
 * progress, EmptyState with icon + CTA, SkeletonCardList while loading,
 * larger tap targets (56px tile min-height), haptics on refresh.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AssignedEventEnvelope } from '@argos/api-types';
import {
  RefreshCw,
  Settings,
  LogOut,
  CalendarDays,
  ChevronRight,
  Plus,
} from 'lucide-react';
import { useSession } from '../hooks/useSession';
import { setSession } from '../auth/session';
import { getDb } from '../db';
import { runBootstrapSync } from '../sync/bootstrap';
import { SkeletonCardList } from '../components/Skeleton';
import { EmptyState } from '../components/EmptyState';
import { STATUS_TONES, ICON_BTN_CLS, BTN_PRIMARY_CLS } from '../styles/tokens';
import * as haptics from '../lib/haptics';
import clsx from 'clsx';

interface EventTile {
  id: string;
  name: string;
  site_name: string;
  status: 'planned' | 'in_progress' | 'complete';
  total_wells: number;
  wells_completed: number;
  scheduled_start?: string;
}

export function EventsPage() {
  const session = useSession();
  const navigate = useNavigate();
  const [tiles, setTiles] = useState<EventTile[] | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadFromCache = async () => {
    try {
      const db = getDb();
      const rows = await db.cached_events
        .filter((r) => r.status !== 'complete')
        .toArray();
      const next: EventTile[] = rows.map((r) => {
        const env = r.data as AssignedEventEnvelope;
        return {
          id: env.event.id,
          name: env.event.name,
          site_name: env.event.site_name ?? '',
          status: env.event.status,
          total_wells: env.event.total_wells ?? 0,
          wells_completed: env.event.wells_completed ?? 0,
          scheduled_start: env.event.scheduled_start,
        };
      });
      next.sort((a, b) =>
        (a.scheduled_start ?? '').localeCompare(b.scheduled_start ?? ''),
      );
      setTiles(next);
    } catch (err) {
      setError((err as Error).message);
      setTiles([]);
    }
  };

  useEffect(() => {
    void loadFromCache();
  }, []);

  const refresh = async () => {
    haptics.tap();
    setRefreshing(true);
    setError(null);
    try {
      const result = await runBootstrapSync();
      if (!result.ok) {
        setError(result.error ?? 'Sync failed');
      }
      await loadFromCache();
    } finally {
      setRefreshing(false);
    }
  };

  const lock = () => {
    haptics.tap();
    setSession(null);
    navigate('/unlock', { replace: true });
  };

  return (
    <div className="flex-1 flex flex-col p-6 gap-4">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-mono-100 tracking-tight">Today's events</h1>
          <p className="text-mono-400 text-sm mt-1 truncate">
            {session?.user.email}
            {session?.user.role && (
              <span className="ml-2 text-[10px] uppercase tracking-widest text-mono-500">
                {session.user.role}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            className={ICON_BTN_CLS}
            onClick={refresh}
            disabled={refreshing}
            aria-label="Refresh"
            title="Refresh"
          >
            <RefreshCw className={clsx('h-5 w-5', refreshing && 'animate-spin')} strokeWidth={2} />
          </button>
          <button
            type="button"
            className={ICON_BTN_CLS}
            onClick={() => navigate('/settings')}
            aria-label="Settings"
            title="Settings"
          >
            <Settings className="h-5 w-5" strokeWidth={2} />
          </button>
          <button
            type="button"
            className={ICON_BTN_CLS}
            onClick={lock}
            aria-label="Lock"
            title="Lock"
          >
            <LogOut className="h-5 w-5" strokeWidth={2} />
          </button>
        </div>
      </header>

      {error && (
        <div className="text-accent-red text-sm border border-red-900/40 rounded-md p-2 bg-red-950/20">
          {error}
        </div>
      )}

      {tiles == null ? (
        <SkeletonCardList count={3} />
      ) : tiles.length === 0 ? (
        <EmptyState
          icon={CalendarDays}
          title="No active events"
          hint="When an admin assigns you to a site and creates a sampling event it'll appear here. You can still capture a quick reading without one."
          cta={
            <button
              type="button"
              onClick={() => navigate('/capture')}
              className={`${BTN_PRIMARY_CLS} inline-flex items-center gap-1.5 justify-center !w-auto !px-4`}
            >
              <Plus className="h-4 w-4" strokeWidth={2.4} /> Quick reading
            </button>
          }
        />
      ) : (
        <ul className="space-y-2.5">
          {tiles.map((t) => (
            <EventTileRow key={t.id} tile={t} onTap={() => { haptics.tap(); navigate(`/events/${t.id}`); }} />
          ))}
        </ul>
      )}

      {tiles && tiles.length > 0 && (
        <button
          type="button"
          className="btn-secondary w-full inline-flex items-center justify-center gap-1.5"
          onClick={() => { haptics.tap(); navigate('/capture'); }}
        >
          <Plus className="h-4 w-4" strokeWidth={2.4} />
          Quick reading (no event)
        </button>
      )}
    </div>
  );
}

function EventTileRow({ tile, onTap }: { tile: EventTile; onTap: () => void }) {
  const tone = STATUS_TONES[tile.status === 'in_progress' ? 'in_progress' : tile.status === 'complete' ? 'complete' : 'planned'];
  return (
    <li>
      <button
        type="button"
        className="w-full text-left rounded-xl border border-mono-800 bg-mono-900/40 hover:bg-mono-900/60 active:bg-mono-900 transition-colors p-4 min-h-[56px] flex items-center gap-3 group"
        onClick={onTap}
      >
        <div className={clsx('h-12 w-1 rounded-full shrink-0', tone.bar)} aria-hidden />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="min-w-0">
              <div className="font-semibold text-mono-100 truncate">{tile.name}</div>
              <div className="text-[12px] text-mono-400 truncate">
                {tile.site_name}
                {tile.scheduled_start && <span className="text-mono-500"> · {tile.scheduled_start}</span>}
              </div>
            </div>
            <span className={clsx('text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded ring-1', tone.bg, tone.text, tone.ring)}>
              {tile.status === 'in_progress' ? 'In progress' : tile.status === 'complete' ? 'Complete' : 'Planned'}
            </span>
          </div>
          <ProgressBar total={tile.total_wells} completed={tile.wells_completed} />
        </div>
        <ChevronRight className="h-5 w-5 text-mono-500 group-hover:text-cyan-400 transition-colors shrink-0" strokeWidth={2} />
      </button>
    </li>
  );
}

function ProgressBar({ total, completed }: { total: number; completed: number }) {
  const pct = total > 0 ? Math.min(100, Math.round((completed / total) * 100)) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] text-mono-400 mb-1">
        <span>{completed} of {total} wells</span>
        <span className="font-medium text-mono-300">{pct}%</span>
      </div>
      <div className="h-1.5 bg-mono-800 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-emerald-400 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
